import { Form, Quiz } from '@superfunnel/types';
import Autocomplete from '@superfunnel/ui-core/v2/Autocomplete';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useMemo } from 'react';
import { useDashboardContext } from '../../contexts/DashboardContext';

export type FormOrQuizSelection =
  | { type: 'form'; id: string }
  | { type: 'quiz'; id: string }
  | null;

interface Option {
  type: 'form' | 'quiz';
  id: string;
  name: string;
  doc: QueryDocumentSnapshot<Form> | QueryDocumentSnapshot<Quiz>;
}

interface Props {
  title?: string;
  /** ID of the selected form or quiz */
  formId?: string;
  onChange?: (selection: FormOrQuizSelection) => void;
  disabled?: boolean;
}

export function FormOrQuizSelector({
  title,
  formId,
  onChange,
  disabled,
}: Props) {
  const { forms, quizzes } = useDashboardContext();

  const options = useMemo((): Option[] => {
    const result: Option[] = [];

    // Add forms
    if (forms) {
      for (const doc of forms) {
        const form = doc.data();
        result.push({
          type: 'form',
          id: doc.id,
          name: form.name || 'Untitled Form',
          doc,
        });
      }
    }

    // Add quizzes
    if (quizzes) {
      for (const doc of quizzes) {
        const quiz = doc.data();
        result.push({
          type: 'quiz',
          id: doc.id,
          name: quiz.name || 'Untitled Quiz',
          doc,
        });
      }
    }

    return result;
  }, [forms, quizzes]);

  const selectedOption = useMemo(() => {
    if (!formId) return null;
    // Search both forms and quizzes with the same ID
    return options.find((o) => o.id === formId) ?? null;
  }, [options, formId]);

  const isLoading = forms === undefined || quizzes === undefined;

  return (
    <Autocomplete
      title={title}
      selected={selectedOption}
      onChange={(option) => {
        if (!option) {
          onChange?.(null);
        } else {
          onChange?.({ type: option.type, id: option.id });
        }
      }}
      options={options}
      getValue={(option) =>
        `[${option.type === 'form' ? 'Form' : 'Quiz'}] ${option.name}`
      }
      getOptionId={(option) => `${option.type}:${option.id}`}
      placeholder="Select form or quiz..."
      disabled={disabled || isLoading}
      modal
    />
  );
}
